import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MobileKycPage } from "./MobileKycPage";
import { WebQRRedirect } from "./WebQRRedirect";
import "./kycModal.css";

interface MobileKycRouterProps {
  serverKey?: string;
}

/**
 * Mobile KYC Router Component
 * Handles routing for mobile KYC continuation
 * Usage: <MobileKycRouter serverKey="your-server-key" />
 */
export const MobileKycRouter: React.FC<MobileKycRouterProps> = ({ serverKey }) => {
  try { console.log('[MobileKycRouter] render', { hasServerKeyProp: Boolean(serverKey) }); } catch {}
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isWebDevice, setIsWebDevice] = useState(false);
  const [effectiveKey, setEffectiveKey] = useState<string | null>(serverKey || null);

  // Extract session ID from URL path or QR code scan
  const getSessionIdFromUrl = (): string | null => {
    const path = window.location.pathname;
    const hash = window.location.hash || "";
    // Prefer clean path; fall back to hash if present
    const pathMatch = path.match(/\/mobile-kyc\/([a-f0-9-]+)/i);
    if (pathMatch) return pathMatch[1];
    const hashMatch = hash.match(/mobile-kyc\/([a-f0-9-]+)/i);
    return hashMatch ? hashMatch[1] : null;
  };

  // Handle QR code scanning
  const handleQRCodeScan = (qrData: string) => {
    try {
      try { console.log('[MobileKycRouter] handleQRCodeScan', { qrData }); } catch {}
      // Accept multiple formats:
      // 1) Full URL like https://host/mobile-kyc/<sessionId>
      // 2) "mobile-kyc:<sessionId>"
      // 3) Raw <sessionId>
      let sessionIdFromQR = qrData;
      // Extract from URL path if present
      const urlMatch = qrData.match(/\/mobile-kyc\/(.*?)($|[?#\s])/i);
      if (urlMatch && urlMatch[1]) {
        sessionIdFromQR = decodeURIComponent(urlMatch[1]);
      } else if (qrData.includes(':')) {
        // Fallback to colon-delimited schema
        sessionIdFromQR = qrData.split(':').pop() || qrData;
      }
      
      if (sessionIdFromQR && sessionIdFromQR.match(/^[a-z0-9-]+$/i)) {
        setSessionId(sessionIdFromQR);
        setIsScanning(false);
        // Preserve existing query string (e.g., ?mode=face) and normalize to path (no hash)
        const search = window.location.search || '';
        window.history.pushState({}, '', `/mobile-kyc/${sessionIdFromQR}${search}`);
      } else {
        toast.error('Invalid QR code. Please scan the QR code from the web version.');
      }
    } catch (error) {
      console.error('Error parsing QR code:', error);
      toast.error('Error reading QR code. Please try again.');
    }
  };

  // Detect if user is on mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Initialize session ID from URL
  useEffect(() => {
    const urlSessionId = getSessionIdFromUrl();
    const isMobile = isMobileDevice();
    
    setIsWebDevice(!isMobile);
    try {
      const params = new URLSearchParams(window.location.search || "");
      const key = params.get("key");
      if (!effectiveKey && key) setEffectiveKey(key);
      try { console.log('[MobileKycRouter] effect:init', { urlSessionId, isMobile, key }); } catch {}
    } catch {}
    
    if (urlSessionId) {
      setSessionId(urlSessionId);
      // If URL had a hash-based form, normalize to clean path while preserving query params
      const hadHash = (window.location.hash || '').includes('/mobile-kyc/');
      if (hadHash) {
        const search = window.location.search || '';
        window.history.replaceState({}, '', `/mobile-kyc/${urlSessionId}${search}`);
      }
    } else {
      setIsScanning(true);
    }
  }, []);

  // QR Code Scanner Component
  const QRCodeScanner = () => (
    <div className="qr-fullscreen">
      <div style={{ 
        textAlign: "center", 
        padding: "24px",
        background: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        maxWidth: "400px",
        width: "100%",
        marginTop: "auto",
        marginBottom: "auto",
        flexShrink: 0
      }}>
        <h1 style={{ margin: "0 0 16px 0", color: "#1f2937", fontSize: "24px" }}>
          Scan QR Code
        </h1>
        <p style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: "16px" }}>
          Scan the QR code from the web version to continue your KYC on mobile.
        </p>
        
        {/* Simple QR Code Input for testing - in production, use a proper QR scanner library */}
        <div style={{ marginBottom: "16px" }}>
          <input
            type="text"
            placeholder="Paste QR code data here (for testing)"
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              marginBottom: "12px"
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleQRCodeScan(e.currentTarget.value);
              }
            }}
          />
          <button
            onClick={() => {
              const input = document.querySelector('input[type="text"]') as HTMLInputElement;
              if (input?.value) {
                handleQRCodeScan(input.value);
              }
            }}
            style={{
              width: "100%",
              padding: "12px",
              background: "#1f6feb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              cursor: "pointer"
            }}
          >
            Continue
          </button>
        </div>

        <p style={{ margin: "0", color: "#9ca3af", fontSize: "12px" }}>
          For production: Integrate with a QR code scanner library like 'qr-scanner' or 'html5-qrcode'
        </p>
      </div>
    </div>
  );

  // If on web device and has session ID, show redirect page with option to continue
  if (isWebDevice && sessionId) {
    try { console.log('[MobileKycRouter] render:WebQRRedirect', { sessionId, hasKey: Boolean(effectiveKey) }); } catch {}
    return <WebQRRedirect sessionId={sessionId} serverKey={effectiveKey || ""} />;
  }

  if (isScanning) {
    try { console.log('[MobileKycRouter] render:QRCodeScanner'); } catch {}
    return <QRCodeScanner />;
  }

  if (!sessionId) {
    try { console.log('[MobileKycRouter] render:NoSession'); } catch {}
    return (
      <div className="qr-fullscreen">
        <div style={{ 
          textAlign: "center", 
          padding: "24px",
          background: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          marginTop: "20px",
          maxWidth: "400px",
          width: "100%"
        }}>
          <h1 style={{ margin: "0 0 16px 0", color: "#dc2626" }}>Invalid Session</h1>
          <p style={{ margin: "0", color: "#6b7280" }}>
            Please scan the QR code from the web version to continue your KYC.
          </p>
        </div>
      </div>
    );
  }

  try { console.log('[MobileKycRouter] render:MobileKycPage', { sessionId, hasKey: Boolean(effectiveKey) }); } catch {}
  return <MobileKycPage sessionId={sessionId} serverKey={effectiveKey || ""} />;
};
